<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const tokenInput = ref(props.agent.custom_wavoip_token || '');
watch(
  () => props.agent.id,
  () => {
    tokenInput.value = props.agent.custom_wavoip_token || '';
  }
);
const isSaving = ref(false);
const inputType = ref('password');

const toggleMasked = () => {
  inputType.value = inputType.value === 'password' ? 'text' : 'password';
};

const maskIcon = computed(() =>
  inputType.value === 'password' ? 'eye-hide' : 'eye-show'
);

const toggleAriaLabel = computed(() =>
  t(
    maskIcon.value === 'eye-hide'
      ? 'CDC.WAVOIP.admin.reveal'
      : 'CDC.WAVOIP.admin.hide'
  )
);

const saveToken = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('agents/update', {
      id: props.agent.id,
      custom_wavoip_token: tokenInput.value?.trim() || null,
    });
    useAlert(t('CDC.WAVOIP.admin.save_success'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    useAlert(t('CDC.WAVOIP.admin.save_error'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div v-if="isAdmin" class="flex flex-col gap-2 mb-4">
    <label class="text-sm font-medium text-n-slate-12">
      {{ t('CDC.WAVOIP.admin.label') }}
    </label>
    <div class="flex flex-row justify-between gap-4">
      <woot-input
        v-model="tokenInput"
        name="custom_wavoip_token"
        class="flex-1 [&>input]:!py-1.5 ltr:[&>input]:!pr-9 ltr:[&>input]:!pl-3 rtl:[&>input]:!pl-9 rtl:[&>input]:!pr-3 focus:[&>input]:!border-n-weak relative"
        :styles="{
          borderRadius: '12px',
          fontSize: '14px',
          marginBottom: '2px',
        }"
        :type="inputType"
        :placeholder="t('CDC.WAVOIP.admin.placeholder')"
        @keydown.enter.prevent="saveToken"
      >
        <template #masked>
          <button
            class="absolute top-0 bottom-0 ltr:right-0.5 rtl:left-0.5"
            type="button"
            :aria-label="toggleAriaLabel"
            @click="toggleMasked"
          >
            <fluent-icon :icon="maskIcon" :size="16" />
          </button>
        </template>
      </woot-input>
      <NextButton
        :label="t('CDC.WAVOIP.admin.save_button')"
        type="button"
        :is-loading="isSaving"
        :disabled="isSaving"
        class="rounded-xl"
        @click="saveToken"
      />
    </div>
    <span class="text-xs text-n-slate-11">
      {{ t('CDC.WAVOIP.admin.helper') }}
    </span>
  </div>
</template>
